'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useIntercomState } from '@/state/intercomState';

// Extend window interface for Intercom
declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: {
      api_base?: string;
      app_id?: string;
    };
  }
}

// Intercom App ID - safe to expose on client side (public identifier)
const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

const IntercomProvider = () => {
  const { user, status } = useUserLogged();
  const { resolvedTheme } = useTheme();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { setIsOpen, setUnreadCount } = useIntercomState();

  // Initialize Intercom after script loads
  const handleScriptLoad = () => {
    if (typeof window === 'undefined') return;

    // Set intercom settings before initialization
    window.intercomSettings = {
      api_base: 'https://api-iam.intercom.io',
      app_id: APP_ID,
    };

    setScriptLoaded(true);
  };

  // Initialize/update Intercom when script is loaded or user/status changes
  useEffect(() => {
    if (!scriptLoaded || typeof window === 'undefined' || !window.Intercom)
      return;

    const intercom = window.Intercom;
    if (!intercom) return;

    const updateIntercomColors = () => {
      const actionColor = resolvedTheme === 'dark' ? '#FFFFFF' : '#000000';
      const backgroundColor = '#A2E435';

      // Authenticated user - boot with user data
      if (status === 'authenticated' && user?.UserUUID) {
        const userFullName =
          user.FirstName && user.LastName
            ? `${user.FirstName} ${user.LastName}`.trim()
            : user.FirstName || user.LastName || user.name || undefined;

        try {
          intercom('boot', {
            app_id: APP_ID,
            user_id: user.UserUUID,
            name: userFullName,
            email: user.Email || user.email || undefined,
            hide_default_launcher: true,
            action_color: actionColor,
            background_color: backgroundColor,
            alignment: 'left',
            horizontal_padding: 252,
            vertical_padding: 20,
          });
        } catch (error) {
          // Boot error
        }
      }
      // Unauthenticated - shutdown or boot anonymously
      else if (status === 'unauthenticated') {
        try {
          intercom('shutdown');
          intercom('boot', {
            app_id: APP_ID,
            action_color: actionColor,
            background_color: backgroundColor,
            hide_default_launcher: true,
            alignment: 'left',
            horizontal_padding: 252,
            vertical_padding: 20,
          });
        } catch (error) {
          // Shutdown/Boot error
        }
      }
    };

    updateIntercomColors();

    // Track open/close and unread count
    intercom('onShow', () => setIsOpen(true));
    intercom('onHide', () => setIsOpen(false));
    intercom('onUnreadCountChange', (count: number) => setUnreadCount(count));
  }, [
    scriptLoaded,
    resolvedTheme,
    user?.UserUUID,
    user?.FirstName,
    user?.LastName,
    user?.Email,
    user?.email,
    user?.name,
    status,
    setIsOpen,
    setUnreadCount,
  ]);

  return (
    <>
      {/* Use Next.js Script component with lazyOnload strategy (recommended for chat widgets) */}
      <Script
        id="intercom-widget"
        src={`https://widget.intercom.io/widget/${APP_ID}`}
        strategy="lazyOnload"
        onLoad={handleScriptLoad}
        onError={(e) => {
          console.error('Failed to load Intercom script', e);
        }}
      />
    </>
  );
};

export default IntercomProvider;
